import { Add, Edit } from '@mui/icons-material'
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography
} from '@mui/material'
import { ExplanationAccordion } from 'components'
import * as controllers from 'controllers'
import { getImportBranches } from 'controllers'
import { PortOfLadingDto } from 'dtos'
import { SortDirection } from 'localConstants/sortDirection'
import { enqueueSnackbar } from 'notistack'
import { PortOfLadingParameters } from 'parameters'
import { useEffect, useState } from 'react'
import { useDebounce } from 'utils'
import PortOfLadingMaintenanceAddEditDialog from './PortOfLadingMaintenanceAddEditDialog'

export default function PortOfLadingMaintenanceListCard() {
  const [isGettingPortOfLading, setIsGettingPortOfLading] = useState<boolean>(false)
  const [isPortOfLadingMaintenanceSaving, setIsPortOfLadingMaintenanceSaving] = useState<boolean>(false)
  const [portOfLading, setPortOfLading] = useState<PortOfLadingDto>(new PortOfLadingDto())
  const [PortOfLadingMaintenanceAddEditDialogOpen, setPortOfLadingMaintenanceAddEditDialogOpen] = useState<boolean>(false)
  const [portsOfLading, setPortsOfLading] = useState<PortOfLadingDto[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [parameters, setParameters] = useState<{
    page: number
    pageSize: number
    orderBy: string
    order: SortDirection
  }>({
    page: 0,
    pageSize: 10,
    orderBy: 'portOfLading',
    order: 'desc'
  })

  useEffect(() => {
    getPortsOfLading(parameters)
  }, [parameters])

  const getPortsOfLading = useDebounce((parameters: PortOfLadingParameters) => {
    setIsGettingPortOfLading(true)
    controllers
      .getPortOfLading(parameters)
      .then(response => {
        setTotalCount(response.totalCount ?? 0)
        setPortsOfLading(response.value)
      })
      .finally(() => {
        setIsGettingPortOfLading(false)
      })
  }, 100)

  return (
    <>
      <PortOfLadingMaintenanceAddEditDialog
        initialValues={portOfLading}
        isLoading={isPortOfLadingMaintenanceSaving}
        onClose={() => {
          setPortOfLadingMaintenanceAddEditDialogOpen(false)
        }}
        onSave={(values: PortOfLadingDto) => {
          const requestMethod = values.portOfLadingGuid ? controllers.updatePortOfLading : controllers.createPortOfLading

          setIsPortOfLadingMaintenanceSaving(true)
          requestMethod(values)
            .then(() => {
              setPortOfLadingMaintenanceAddEditDialogOpen(false)
              enqueueSnackbar('Port Of Lading Saved Successfully!', { variant: 'success' })
              getPortsOfLading(parameters)
              getImportBranches()
            })
            .finally(() => {
              setIsPortOfLadingMaintenanceSaving(false)
            })
        }}
        open={PortOfLadingMaintenanceAddEditDialogOpen}
      />

      <Card>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto'>
                <Typography fontWeight='bold' variant='h5'>
                  Ports of Lading Maintenance
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                <Button
                  onClick={() => {
                    setPortOfLading(new PortOfLadingDto())
                    setPortOfLadingMaintenanceAddEditDialogOpen(true)
                  }}
                  variant='text'
                  endIcon={<Add />}
                >
                  ADD
                </Button>
              </Grid>
            </Grid>

            {isGettingPortOfLading && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                <Grid>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingPortOfLading && (
              <Grid item xs={12}>
                <Card variant='outlined'>
                  <CardContent>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={theme => ({ color: theme.palette.primary.main })}
                              sortDirection={parameters.orderBy === 'cityProvince' ? parameters.order : false}
                            >
                              <TableSortLabel
                                active={parameters.orderBy === 'cityProvince'}
                                direction={parameters.orderBy === 'cityProvince' ? parameters.order : undefined}
                                onClick={() => {
                                  setParameters({
                                    ...parameters,
                                    order: parameters.orderBy === 'cityProvince' && parameters.order === 'asc' ? 'desc' : 'asc',
                                    orderBy: 'cityProvince'
                                  })
                                }}
                              >
                                Port Of Lading
                              </TableSortLabel>
                            </TableCell>

                            <TableCell
                              sx={theme => ({ color: theme.palette.primary.main })}
                              sortDirection={parameters.orderBy === 'importBranches' ? parameters.order : false}
                            >
                              <TableSortLabel
                                active={parameters.orderBy === 'importBranches'}
                                direction={parameters.orderBy === 'importBranches' ? parameters.order : undefined}
                                onClick={() => {
                                  setParameters({
                                    ...parameters,
                                    order:
                                      parameters.orderBy === 'importBranches' && parameters.order === 'asc' ? 'desc' : 'asc',
                                    orderBy: 'importBranches'
                                  })
                                }}
                              >
                                Import Branches
                              </TableSortLabel>
                            </TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {portsOfLading.map((portOfLading, index) => (
                            <TableRow
                              key={portOfLading.portOfLadingGuid}
                              sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}
                            >
                              <TableCell>{portOfLading.cityProvince}</TableCell>

                              <TableCell>
                                {portOfLading.importBranches?.map(importBranch => importBranch.name).join(', ')}
                              </TableCell>

                              <TableCell padding='checkbox'>
                                <Button
                                  color='primary'
                                  endIcon={<Edit />}
                                  onClick={() => {
                                    setPortOfLading(portOfLading)
                                    setPortOfLadingMaintenanceAddEditDialogOpen(true)
                                  }}
                                  variant='text'
                                >
                                  EDIT
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>

                        <TableFooter>
                          <TableRow>
                            <TablePagination
                              rowsPerPageOptions={[5, 10, 25]}
                              SelectProps={{ fullWidth: false }}
                              count={totalCount}
                              rowsPerPage={parameters.pageSize}
                              page={parameters.page}
                              onPageChange={(_, page) => {
                                setParameters({
                                  ...parameters,
                                  page
                                })
                              }}
                              onRowsPerPageChange={e => {
                                setParameters({
                                  ...parameters,
                                  page: 0,
                                  pageSize: e.target.value as unknown as number
                                })
                              }}
                            />
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            )}

            <Grid item xs={12}>
              <ExplanationAccordion variant='outlined'>
                ||Click add to add a new record.|| Click edit to edit an existing record. ||
              </ExplanationAccordion>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
